/**
 * W7 — Multi-track PLAN timeline (editor). The §13/13a planner as an *authoring*
 * tool: tracks Fleet→Pad→Well→Section with the CPE (rig/frac/CT) spine, editable
 * plan bars, milestones, and the look-ahead horizon. Authors create / sequence /
 * re-time plan bars against the plan's programa; actual + NPT bars overlay
 * read-only (fed back from Oxbo-Z) so the author sequences against reality.
 *
 * The Gantt is hand-SVG/CSS (no Plotly / no Gantt lib). Editing works two ways:
 * a visual timeline (click to select, drag a plan bar to re-time) AND a per-track
 * row grid (add / edit dates & depth / reorder / delete) that reflects the same
 * node list — so the plan is fully authorable even without pointer drag. Wired to
 * the pApi `plan-timeline` endpoint (GET timeline / PUT plan nodes), MSW-seeded.
 * Built to w7-plan-timeline.html on the P3 kit.
 */
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import {
  useGetPlanQuery,
  useGetPlanTimelineQuery,
  usePutPlanTimelineMutation,
} from "@api/pApi";
import type { TimelineNode, TimelineTrack } from "@api/types";
import type { MessageKey } from "@i18n/messages";
import { QueryState } from "@components/ui/QueryState";
import { enrichEventCode, EVENT_LETTER_OP_VAR } from "@lib/eventEnrichment";
import { intlTag } from "@lib/format";
import { GanttCanvas } from "./GanttCanvas";
import { BarEditor } from "./BarEditor";
import { TrackGrid } from "./TrackGrid";
import {
  type PaFilter,
  type Zoom,
  deleteNode,
  layoutTracks,
  makePlanNode,
  makeScale,
  opsPresent,
  placeNodes,
  planNodeCount,
  readNodes,
  readTracks,
  reorderInTrack,
  shiftNodeDays,
  updateNode,
} from "./timelineModel";
import "./timeline.css";

/** Fallback plan the screen opens against when W0 hasn't set an active plan. */
const DEFAULT_PLAN_ID = "pl-fp1216-pnc";

/** All op classes the filter can toggle (order = legend order). */
const OP_ORDER = ["pad", "drilling", "unconv", "completion", "wireline", "ct", "workover"];

export function PlanTimeline() {
  const { t, locale } = useLocale();
  const tag = intlTag(locale);
  const activePlanId = useAppSelector((s) => s.wellContext.activePlanId);
  const planId = activePlanId ?? DEFAULT_PLAN_ID;

  const planQ = useGetPlanQuery(planId);
  const timelineQ = useGetPlanTimelineQuery(planId);
  const [saveTimeline, saveState] = usePutPlanTimelineMutation();

  // Editable copy of the plan nodes; the server stream seeds it, a re-fetch
  // reseeds. The canvas + grid both read + write this one list.
  const [nodes, setNodes] = useState<TimelineNode[]>([]);
  const [dirty, setDirty] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Controls
  const [zoom, setZoom] = useState<Zoom>("week");
  const [pa, setPa] = useState<PaFilter>("both");
  const [showToday, setShowToday] = useState(true);
  const [opFilter, setOpFilter] = useState<Set<string>>(new Set(OP_ORDER));

  // ★ asArray-guarded ingest: a real backend can return a present-but-non-array
  // `nodes` / `tracks` (`{}`, `null`, an error envelope) that `?? []` and a
  // truthy `if` both admit — the next `.map` would white-screen the app (the
  // Oxbo-Z prod crash class). readNodes/readTracks coerce + normalise here.
  const serverData = timelineQ.data;
  const tracks = useMemo(() => readTracks(serverData), [serverData]);
  useEffect(() => {
    setNodes(readNodes(serverData));
    setDirty(false);
    setSelectedId(null);
  }, [serverData]);

  // Horizon / today fall back to a safe window so an empty body still renders
  // an axis + Today line rather than throwing.
  const todayISO = serverData?.today ?? new Date().toISOString().slice(0, 10);
  const horizonStart = serverData?.horizon_start ?? todayISO;
  const horizonEnd = serverData?.horizon_end ?? todayISO;
  const lookaheadWeeks = serverData?.lookahead_weeks ?? 4;

  const scale = useMemo(() => makeScale(horizonStart, horizonEnd, zoom), [horizonStart, horizonEnd, zoom]);
  const laid = useMemo(() => layoutTracks(tracks), [tracks]);
  const placed = useMemo(
    () => placeNodes(nodes, laid, scale, { pa, ops: opFilter }),
    [nodes, laid, scale, pa, opFilter],
  );

  const presentOps = useMemo(() => new Set(opsPresent(nodes)), [nodes]);
  const planCount = planNodeCount(nodes);
  const selected = nodes.find((n) => n.id === selectedId) ?? null;

  const plan = planQ.data;
  const enr = enrichEventCode(plan?.event_code);
  const opVarForEvent = enr.letter ? EVENT_LETTER_OP_VAR[enr.letter] : "--op-unconv";

  function commit(next: TimelineNode[]) {
    setNodes(next);
    setDirty(true);
  }

  function patchSelected(patch: Partial<TimelineNode>) {
    if (!selectedId) return;
    commit(updateNode(nodes, selectedId, patch));
  }

  function removeSelected() {
    if (!selectedId) return;
    commit(deleteNode(nodes, selectedId));
    setSelectedId(null);
  }

  function addStep(track: TimelineTrack) {
    const node = makePlanNode(track, todayISO, nodes);
    commit([...nodes, node]);
    setSelectedId(node.id);
  }

  async function save() {
    // Persist only what W owns — the plan nodes. Actual/npt overlays are the
    // server's; we send them back untouched so a headless save is idempotent.
    await saveTimeline({ planId, nodes }).unwrap();
    setDirty(false);
  }

  function toggleOp(op: string) {
    setOpFilter((prev) => {
      const next = new Set(prev);
      if (next.has(op)) next.delete(op);
      else next.add(op);
      return next;
    });
  }

  return (
    <div>
      <div className="screen-head">
        <h1>{t("timeline.title")}</h1>
        <span className="badge info">{t("timeline.badge")}</span>
        <div className="actions">
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={!dirty || saveState.isLoading}
          >
            <span className="ic ic-check" aria-hidden="true" />
            {saveState.isLoading ? t("timeline.saving") : t("timeline.save")}
          </button>
        </div>
      </div>

      {/* event / plan context */}
      {plan && (
        <div className="chip-ctx" style={{ marginBottom: 14, flexWrap: "wrap" }}>
          <span className="k">{t("timeline.ctx.well")}</span>
          <span className="mono">{plan.well_uwi}</span>
          <span className="dim">·</span>
          <span className="k">{t("timeline.ctx.event")}</span>
          <span className="letter-badge" style={{ background: `var(${opVarForEvent})` }}>
            <span className="sw" style={{ background: "var(--head)" }} />
            {enr.letter ?? "?"} · {plan.event_code}
          </span>
          <span className="verchip">v{plan.version}</span>
        </div>
      )}

      <p className="section-note">
        <span className="qm">i</span>
        <span>{t("timeline.note")}</span>
      </p>

      <QueryState
        isLoading={timelineQ.isLoading}
        isError={timelineQ.isError}
        onRetry={() => void timelineQ.refetch()}
      >
        {/* controls */}
        <div className="tl-ctrl-row">
          <div className="grp">
            <span className="lbl">{t("timeline.zoom")}</span>
            <div className="seg" role="group" aria-label={t("timeline.zoom")}>
              {(["day", "week", "month"] as Zoom[]).map((z) => (
                <button key={z} type="button" className={z === zoom ? "on" : ""} onClick={() => setZoom(z)}>
                  {t(`timeline.zoom.${z}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="grp">
            <span className="lbl">{t("timeline.show")}</span>
            <div className="seg" role="group" aria-label={t("timeline.show")}>
              {(["both", "plan", "actual"] as PaFilter[]).map((f) => (
                <button key={f} type="button" className={f === pa ? "on" : ""} onClick={() => setPa(f)}>
                  {t(`timeline.pa.${f}`)}
                </button>
              ))}
            </div>
          </div>
          <label className="tl-cb">
            <input type="checkbox" checked={showToday} onChange={(e) => setShowToday(e.target.checked)} />
            {t("timeline.todayLine")}
          </label>
        </div>

        {/* op-filter legend */}
        <div className="op-filter" role="group" aria-label={t("timeline.opFilter")}>
          <span className="oftitle">{t("timeline.operations")}</span>
          {OP_ORDER.filter((op) => presentOps.has(op)).map((op) => (
            <label key={op}>
              <input type="checkbox" checked={opFilter.has(op)} onChange={() => toggleOp(op)} />
              <span className="opsw" style={{ background: `var(--op-${op})` }} />
              {t(`timeline.op.${op}` as MessageKey)}
            </label>
          ))}
        </div>

        {/* legend */}
        <div className="tl-legend">
          <span className="s"><span className="lg-plan" />{t("timeline.legend.plan")}</span>
          <span className="s"><span className="lg-actual" />{t("timeline.legend.actual")}</span>
          <span className="s"><span className="lg-npt" />{t("timeline.legend.npt")}</span>
          <span className="s"><span className="lg-milestone" />{t("timeline.legend.milestone")}</span>
          <span className="s"><span className="lg-lookahead" />{t("timeline.legend.lookahead")}</span>
        </div>

        {tracks.length === 0 ? (
          <div className="empty-state">{t("timeline.empty")}</div>
        ) : (
          <div className="tl-editor-row">
            <div className="gantt-wrap">
              {/* track-label rail */}
              <div className="track-labels">
                {laid.map((tk) => (
                  <div
                    key={tk.id}
                    className={"row-lbl kind-" + tk.kind}
                    style={{ height: tk.height }}
                  >
                    {tk.label}
                    {tk.sub && <span className="sub">{tk.sub}</span>}
                  </div>
                ))}
              </div>
              <GanttCanvas
                laid={laid}
                placed={placed}
                scale={scale}
                zoom={zoom}
                todayISO={todayISO}
                lookaheadWeeks={lookaheadWeeks}
                showToday={showToday}
                selectedId={selectedId}
                intlTag={tag}
                ariaLabel={t("timeline.canvasAria")}
                onSelect={(n) => setSelectedId(n.id)}
                onShiftDays={(id, d) => commit(nodes.map((n) => (n.id === id ? shiftNodeDays(n, d) : n)))}
              />
            </div>

            {/* docked editor — never overlaps the canvas */}
            <BarEditor
              node={selected}
              tracks={tracks}
              onPatch={patchSelected}
              onDelete={removeSelected}
              onClose={() => setSelectedId(null)}
            />
          </div>
        )}

        {/* per-track authoring grid — the drag-free path to a full plan */}
        {tracks.length > 0 && (
          <TrackGrid
            tracks={tracks}
            nodes={nodes}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onPatch={(id, patch) => commit(updateNode(nodes, id, patch))}
            onReorder={(id, dir) => commit(reorderInTrack(nodes, id, dir))}
            onDelete={(id) => {
              commit(deleteNode(nodes, id));
              if (id === selectedId) setSelectedId(null);
            }}
            onAdd={addStep}
          />
        )}

        <div className="kpis" style={{ marginTop: 18 }}>
          <Kpi v={String(planCount)} l={t("timeline.kpi.planBars")} />
          <Kpi v={String(tracks.length)} l={t("timeline.kpi.tracks")} />
          <Kpi v={`${lookaheadWeeks}`} u={t("timeline.kpi.weeksUnit")} l={t("timeline.kpi.lookahead")} />
        </div>

        <p className="dim" style={{ fontSize: 12, marginTop: 16, lineHeight: 1.7 }}>
          {t("timeline.footnote")}
        </p>
      </QueryState>
    </div>
  );
}

function Kpi({ v, u, l }: { v: string; u?: string; l: string }) {
  return (
    <div className="kpi" data-own="p">
      <div className="v tab-nums">
        {v}
        {u && <span style={{ fontSize: 12, color: "var(--dim)" }}> {u}</span>}
      </div>
      <div className="l">{l}</div>
    </div>
  );
}
